import React from 'react';
import { motion } from 'framer-motion';
import IndustryPageHeading from '../components/IndustryPageHeading';
import EnterpriseVideo from '../components/EnterpriseVideo';

/**
 * Insurance Solutions — internal route /solutions/industries/insurance
 *
 * Three content sections with alternating layout direction (text/video),
 * mirroring the first three sections of the Logistics Solutions page.
 * Each video slot is an empty champagne-gold-bordered placeholder frame
 * labeled "Video Demo Coming Soon", alternating sides (video right, then
 * left, then right). After the three sections the page goes directly to
 * the site's standard footer, which is rendered globally by Layout.
 */

const VideoDemoFrame = ({ cloudflareVideoId }) => {
  if (!cloudflareVideoId) {
    return (
      <div className="w-full">
        <div
          className="relative w-full overflow-hidden rounded-lg bg-gradient-to-br from-[#E8E4DC] to-[#E8EDE7]"
          style={{
            aspectRatio: '16 / 9',
            border: '2px solid #D4AF37',
            boxShadow:
              'inset 0 2px 8px rgba(20, 30, 48, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
          }}
          data-testid="insurance-video-placeholder"
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-base sm:text-lg text-black/60 font-medium tracking-wide">
              Video Demo Coming Soon
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <EnterpriseVideo
        cloudflareVideoId={cloudflareVideoId}
        title="Insurance demo video"
        testId={`insurance-video-${cloudflareVideoId}`}
        className="w-full rounded-lg bg-gradient-to-br from-[#E8E4DC] to-[#E8EDE7]"
        style={{
          aspectRatio: '16 / 9',
          border: '2px solid #D4AF37',
          boxShadow:
            'inset 0 2px 8px rgba(20, 30, 48, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      />
    </div>
  );
};

const FeatureBlock = ({ title, description }) => (
  <div className="pl-4 relative">
    <div
      className="absolute left-0 top-1 bottom-1 w-0.5 rounded-full"
      style={{
        background: 'linear-gradient(to bottom, #D4AF37, rgba(212, 175, 55, 0.4))',
      }}
    />
    <h3 className="text-base sm:text-lg font-medium text-black/90">{title}</h3>
    <p className="text-sm text-black/70 mt-1">{description}</p>
  </div>
);

const TextContent = ({ eyebrow, heading, body, features, isPageHeading }) => {
  const HeadingTag = isPageHeading ? 'h1' : 'h2';
  return (
    <div>
      <p className="text-sm md:text-base font-bold text-black/90 mb-3 md:mb-4 uppercase tracking-wider">
        {eyebrow}
      </p>
      <HeadingTag className="text-3xl md:text-4xl font-medium text-black/90 mb-4 md:mb-6">
        {heading}
      </HeadingTag>
      <p className="text-base sm:text-lg text-black/70 mb-8 md:mb-10">{body}</p>
      <div className="space-y-6">
        {features.map((f) => (
          <FeatureBlock key={f.title} title={f.title} description={f.description} />
        ))}
      </div>
    </div>
  );
};

const sections = [
  {
    eyebrow: 'Underwriting & Risk Assessment',
    cloudflareVideoId: 'db91a66e0675812e5ffeb61f0be0823f',
    heading: 'Risk, priced in real time.',
    body:
      'GoogLyn reads incoming applications, scores risk against your underwriting guidelines, and returns a bindable quote \u2014 no manual file review, no waiting on an underwriter\u2019s queue.',
    features: [
      {
        title: 'Automated risk scoring',
        description:
          'Applications are assessed against your underwriting rules the moment they arrive.',
      },
      {
        title: 'Instant quote generation',
        description: 'Bindable quotes generated in seconds, not days.',
      },
      {
        title: 'Fraud signals caught upfront',
        description: 'Anomalies flagged before a policy is ever issued.',
      },
    ],
  },
  {
    eyebrow: 'Claims Processing',
    cloudflareVideoId: '4325bad8e1afa151450ba8c16e237656',
    heading: 'First notice of loss to settlement, without the backlog.',
    body:
      'GoogLyn captures claims the moment they\u2019re filed, routes them to the right adjuster, and keeps every party updated automatically \u2014 cutting the cycle time that costs insurers the most.',
    features: [
      {
        title: 'Instant claims intake',
        description:
          'Documentation captured and organized before a human opens the file.',
      },
      {
        title: 'Smart adjuster routing',
        description: 'Claims matched to the right adjuster by type and severity.',
      },
      {
        title: 'Automated status updates',
        description: 'Policyholders always know where their claim stands.',
      },
    ],
  },
  {
    eyebrow: 'Policy Servicing & Compliance',
    cloudflareVideoId: '0f8e4ef522aadbfd1c06790d72b56ff6',
    heading: 'Every policy, always audit-ready.',
    body:
      'Billing, renewals, and regulatory reporting run continuously in the background, so compliance teams work from real-time data instead of quarterly reconstructions.',
    features: [
      {
        title: 'Automated billing & reconciliation',
        description: 'Premium collection and reconciliation without manual intervention.',
      },
      {
        title: 'Proactive renewal management',
        description: 'At-risk policies flagged before they lapse.',
      },
      {
        title: 'Continuous compliance reporting',
        description: 'Audit trails and regulatory filings stay current automatically.',
      },
    ],
  },
];

const IndustryInsurance = () => {
  return (
    <div className="w-full" data-testid="insurance-solutions-page">
      <IndustryPageHeading name="Insurance" testId="insurance-page-heading" />
      {sections.map((section, idx) => {
        const textFirst = idx % 2 === 0; // sections 1,3 → text left; 2 → video left
        const bg = idx % 2 === 0 ? 'bg-white' : 'bg-[#F5F7F5]';
        return (
          <section
            key={section.eyebrow}
            className={`py-16 md:py-24 ${bg}`}
            data-testid={`insurance-section-${idx + 1}`}
          >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center"
              >
                {textFirst ? (
                  <>
                    <TextContent {...section} isPageHeading={idx === 0} />
                    <VideoDemoFrame cloudflareVideoId={section.cloudflareVideoId} />
                  </>
                ) : (
                  <>
                    {/* Video first on desktop; text stays above video on mobile for readability */}
                    <div className="order-2 lg:order-1">
                      <VideoDemoFrame cloudflareVideoId={section.cloudflareVideoId} />
                    </div>
                    <div className="order-1 lg:order-2">
                      <TextContent {...section} isPageHeading={idx === 0} />
                    </div>
                  </>
                )}
              </motion.div>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default IndustryInsurance;
